<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import BasePercentageBar from '@/components/base/BasePercentageBar.vue';

import { useSummariesStore } from '@/stores/summaries.store';
import { formatMoney, getTranslation } from '@/composables/data-display-utils';
import type {
  Locales,
  SummaryFormStatDotVotingChoiceDto,
  SummaryFormStatDotVotingDto,
} from '@/services/types/api.types';
import { Constants } from '@/constants';

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  formId: number;
}>();

// STORE
const summariesStore = useSummariesStore();
const formStat = computed(() => summariesStore.formStatsById(props.formId));

// CONST
const { t } = useI18n();

// COMPUTED
const dotVotingStats = computed<SummaryFormStatDotVotingDto | null>(() => {
  if (formStat.value?.type !== Constants.Form.Type.DotVoting) return null;
  return formStat.value.stats as SummaryFormStatDotVotingDto;
});

const dots = computed(() => {
  if (!dotVotingStats.value) return null;
  return dotVotingStats.value.dots ?? null;
});

const dotVotingType = computed(() => {
  if (!dotVotingStats.value) return null;
  return dotVotingStats.value.dot_voting_type ?? null;
});

const dotVotingMeasurementType = computed(() => {
  if (!dotVotingStats.value) return null;
  return dotVotingStats.value.dot_voting_measurement_type ?? null;
});

const variableStatsByDot = computed<Record<number, string[]>>(() => {
  const stats: Record<number, string[]> = {};
  if (dotVotingType.value !== Constants.Form.DotVoting.Type.Variable || !dots.value?.length) return stats;

  const formatPoints = (value: number | null) => {
    if (value === null) return null;
    return `${value} ${t('form.dot-voting.points-abbreviation')}`;
  };

  const formatCurrency = (value: number | null) => {
    if (value === null) return null;
    return formatMoney(value);
  };

  const buildDotStats = (dot: SummaryFormStatDotVotingChoiceDto): string[] => {
    const items: string[] = [];
    const averageRaw = Number(dot.distribution?.average);
    const average = Number.isFinite(averageRaw) ? averageRaw : null;
    const medianRaw = Number(dot.distribution?.median);
    const median = Number.isFinite(medianRaw) ? medianRaw : null;

    const dotCost = Number(dot.cost);
    const costValue = Number.isFinite(dotCost) ? dotCost : null;

    if (dotVotingMeasurementType.value === Constants.Form.DotVoting.MeasurementType.Points) {
      const averageLabel = average !== null ? formatPoints(average) : null;
      if (averageLabel) items.push(`${t('form.dot-voting.results.average')}: ${averageLabel}`);
      const medianLabel = median !== null ? formatPoints(median) : null;
      if (medianLabel) items.push(`${t('form.dot-voting.results.median')}: ${medianLabel}`);
    } else if (dotVotingMeasurementType.value === Constants.Form.DotVoting.MeasurementType.Money) {
      const fundedLabel =
        average !== null && costValue !== null
          ? t('form.dot-voting.results.funded', {
              allocated: formatCurrency(average),
              cost: formatCurrency(costValue),
            })
          : null;
      if (fundedLabel) items.push(fundedLabel);
    }

    return items;
  };

  dots.value?.forEach((dot) => {
    stats[dot.id] = buildDotStats(dot);
  });

  return stats;
});

const getVariableStatsDisplay = (dotId: number): string[] => variableStatsByDot.value[dotId] ?? [];
const getVariableStatsLabel = (dotId: number): string => getVariableStatsDisplay(dotId).join(' | ');

const getDotFillPercentage = (dot: SummaryFormStatDotVotingChoiceDto): number => {
  if (dotVotingType.value === Constants.Form.DotVoting.Type.Fixed) {
    return dot.percentage_of_participants ?? 0;
  } else if (dotVotingType.value === Constants.Form.DotVoting.Type.Variable) {
    return dot.distribution.share_of_total_allocations ?? 0;
  }
  return 0;
};

const questionHasChoiceWithImage = computed((): boolean => !!dots.value?.some((dot) => dot.image));

const isConfigured = computed((): boolean => (dots.value?.length ?? 0) > 0);

const graphTitle = computed((): string => {
  if (!dotVotingType.value) return '';
  return dotVotingType.value === Constants.Form.DotVoting.Type.Fixed
    ? t('form.dot-voting.results.graphTitle-fixed')
    : t('form.dot-voting.results.graphTitle-variable');
});
</script>

<template>
  <slot name="title" :title="graphTitle"></slot>
  <div class="form-dot-voting-question-results">
    <div v-if="isConfigured">
      <q-banner
        v-if="dotVotingType === Constants.Form.DotVoting.Type.Variable"
        class="q-mb-md bg-grey-1 text-grey-8"
        dense
        rounded
      >
        <q-icon name="info" class="q-mr-sm" />
        {{ t('form.dot-voting.results.percentage-variable') }}
      </q-banner>
      <q-banner v-else class="q-mb-md bg-grey-1 text-grey-8" dense rounded>
        <q-icon name="info" class="q-mr-sm" />
        {{ t('form.dot-voting.results.percentage-fixed') }}
      </q-banner>

      <base-percentage-bar
        v-for="dot in dots"
        :key="dot.id"
        :image-src="dot.image"
        :title="getTranslation(dot, 'text', props.languageEnabled)"
        :percentage="getDotFillPercentage(dot)"
        :show-images="questionHasChoiceWithImage"
      >
        <template #meta>
          <div class="form-dot-voting-meta">
            <div class="form-dot-voting-meta__item">
              <q-icon name="person" size="16px" />
              <span>{{ t('global.person', { count: dot.number_of_participants }) }}</span>
            </div>
            <div
              v-if="dotVotingType === Constants.Form.DotVoting.Type.Variable && getVariableStatsDisplay(dot.id).length"
              class="form-dot-voting-meta__item"
            >
              <q-icon name="bar_chart" size="16px" />
              <span>{{ getVariableStatsLabel(dot.id) }}</span>
            </div>
          </div>
        </template>
      </base-percentage-bar>
    </div>
    <div v-else class="text">
      {{ t('form.summary.status.not-configured') }}
    </div>
  </div>
</template>

<style lang="scss">
.form-dot-voting-question-results {
  .text {
    color: $color-neutral-grey-400;
    font-size: $small-text;
  }

  .form-dot-voting-meta {
    display: inline-flex;
    align-items: center;
    gap: $space-sm;
  }

  .form-dot-voting-meta__item {
    display: inline-flex;
    align-items: center;
    gap: $space-xs;
  }
}
</style>
